import React from "react";
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CanalChip, Offer, TabPanel, TooltipCell, TooltipHeaderCell, dataCellSx, formatDate, formatDateWithHours, headerCellSx, parseDMY } from "./ui";

interface OperationsTabProps {
    activeTab: number;
    offers: Offer[];
    loadingOffers: boolean;
    offerPage: number;
    setOfferPage: (page: number) => void;
    offerRowsPerPage: number;
    setOfferRowsPerPage: (rows: number) => void;
    handleOpenMvt: () => void;
    personId: string | number | undefined;
    getOffers: (personId: string | number) => void;
    t: (key: string) => string;
}

const OperationsTab = ({
    activeTab, offers, loadingOffers,
    offerPage, setOfferPage, offerRowsPerPage, setOfferRowsPerPage,
    handleOpenMvt, personId, getOffers, t
}: OperationsTabProps) => (
    <TabPanel value={activeTab} index={2}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
            <Button variant="contained" size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleOpenMvt}>
                Ajouter un mouvement de points
            </Button>
        </Box>
        {loadingOffers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        ) : offers.length === 0 ? (
            <Card variant="outlined">
                <CardContent sx={{ textAlign: "center", py: 6 }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary">Aucune opération trouvée</Typography>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => personId && getOffers(personId)}>
                        Charger les opérations
                    </Button>
                </CardContent>
            </Card>
        ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <colgroup>
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "6%" }} />
                        <col style={{ width: "30%" }} />
                        <col style={{ width: "9%" }} />
                        <col style={{ width: "10%" }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TooltipHeaderCell>Date envoi</TooltipHeaderCell>
                            <TooltipHeaderCell>Libellé</TooltipHeaderCell>
                            <TooltipHeaderCell>Offre</TooltipHeaderCell>
                            <TableCell sx={headerCellSx} />{/* Canal — icône uniquement, pas de titre */}
                            <TooltipHeaderCell>Validité</TooltipHeaderCell>
                            <TooltipHeaderCell sx={{ textAlign: "center" }}>Utilisé</TooltipHeaderCell>
                            <TooltipHeaderCell sx={{ textAlign: "center" }}>Date d'utilisation</TooltipHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...offers].sort((a, b) => parseDMY(b.SENTDATE || b.TRDATE) - parseDMY(a.SENTDATE || a.TRDATE))
                            .slice(offerPage * offerRowsPerPage, (offerPage + 1) * offerRowsPerPage)
                            .map((offer, idx) => {
                                const isUsed = offer.ISUSED === true || offer.ISUSED === "true" || offer.ISUSED === "1" || offer.ISUSED === 1;
                                return (
                                <TableRow key={offer.NUMOPERATION || idx} hover>
                                    <TooltipCell label={formatDate((offer.SENTDATE || offer.TRDATE) as string)}>{formatDate((offer.SENTDATE || offer.TRDATE) as string)}</TooltipCell>
                                    <TooltipCell label={offer.OPERATIONNAME || ""}>{offer.OPERATIONNAME || "—"}</TooltipCell>
                                    <TooltipCell label={offer.OFFERDESCRIPTION || ""}>{offer.OFFERDESCRIPTION || "—"}</TooltipCell>
                                    <TableCell sx={{ ...dataCellSx, overflow: "visible", px: 1, textAlign: "center" }}>
                                        {(offer.CANAL ?? offer['canal']) ? <CanalChip canal={offer.CANAL ?? offer['canal']} /> : "—"}
                                    </TableCell>
                                    <TooltipCell label={offer.STARTDATE && offer.ENDDATE ? `${formatDate(offer.STARTDATE as string)} au ${formatDate(offer.ENDDATE as string)}` : formatDate((offer.STARTDATE || offer.ENDDATE) as string)}>
                                        {offer.STARTDATE && offer.ENDDATE
                                            ? `${formatDate(offer.STARTDATE as string)} au ${formatDate(offer.ENDDATE as string)}`
                                            : formatDate((offer.STARTDATE || offer.ENDDATE) as string)}
                                    </TooltipCell>
                                    <TableCell sx={{ ...dataCellSx, textAlign: "center" }}>
                                        {isUsed
                                            ? <CheckCircleIcon fontSize="small" color="success" />
                                            : <CancelIcon fontSize="small" color="error" />
                                        }
                                    </TableCell>
                                    <TableCell sx={{ ...dataCellSx, textAlign: "center" }}>
                                        {isUsed && offer.TRDATE ? formatDateWithHours(offer.TRDATE as string) : "—"}
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={offers.length}
                    page={offerPage}
                    onPageChange={(_, p) => setOfferPage(p)}
                    rowsPerPage={offerRowsPerPage}
                    onRowsPerPageChange={(e) => { setOfferRowsPerPage(parseInt(e.target.value, 10)); setOfferPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Lignes par page :"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
                />
            </TableContainer>
        )}
    </TabPanel>
);

export default OperationsTab;
